import type { CSSProperties, ReactNode } from 'react'

interface FadeInProps {
  children: ReactNode
  /**
   * Re-key the wrapper to retrigger the animation. Pass the value whose
   * change should produce a fade (e.g. a route path, a query key, a
   * filter). When undefined, the fade only runs once on mount.
   */
  triggerKey?: string | number
  /** Optional translateY in px. 0 = pure fade. Defaults to 0. */
  rise?: number
  /** Override the default token-driven duration. */
  duration?: 'fast' | 'base' | 'slow'
  style?: CSSProperties
  className?: string
  /** Delay before the animation starts. ms. */
  delayMs?: number
}

const DURATION_VAR: Record<NonNullable<FadeInProps['duration']>, string> = {
  fast: 'var(--motion-duration-fast)',
  base: 'var(--motion-duration-base)',
  slow: 'var(--motion-duration-slow)',
}

/**
 * Crossfade wrapper for the moment when content first appears — typically
 * after a `<Skeleton>` is replaced by real data, or when a route mounts.
 * Uses the global `lms-fade-in` / `lms-fade-up` keyframes so the
 * prefers-reduced-motion override at the tokens layer naturally turns
 * this into a no-op for users who opted out.
 *
 * Pass `triggerKey` (e.g. the current pathname or a query-cache key) to
 * remount the wrapper on change so the animation runs again.
 */
export function FadeIn({
  children,
  triggerKey,
  rise = 0,
  duration = 'slow',
  delayMs,
  style,
  className,
}: FadeInProps): JSX.Element {
  const animationName = rise > 0 ? 'lms-fade-up' : 'lms-fade-in'
  const composedStyle: CSSProperties = {
    animation: `${animationName} ${DURATION_VAR[duration]} var(--motion-ease-out) both`,
    ...(delayMs !== undefined ? { animationDelay: `${delayMs}ms` } : {}),
    ...style,
  }
  return (
    <div key={triggerKey} className={className} style={composedStyle}>
      {children}
    </div>
  )
}
